import React, { useEffect, useMemo, useState } from 'react';
import { buildEmailTemplate, EMAIL_TEMPLATE_OPTIONS } from '../../templates/emailTemplates';
import { client } from '../utils/api';

const USERS_CONTROLLER = 'usersController.php';
const EMAIL_CONTROLLER = 'emailController.php';

const initialFormState = {
  searchTerm: '',
  selectedUserIds: [],
  to: '',
  subject: '',
  template: 'RPSwiss',
  message: 'Hello,\n\nWe hope you are well.',
};

export default function SendEmailManager({ prefill = null }) {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState(initialFormState);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (!prefill) return;

    setForm((previous) => ({
      ...previous,
      to: Array.isArray(prefill.recipients) ? prefill.recipients.join(', ') : '',
      subject: prefill.subject || previous.subject,
    }));
    setSearchTerm('');
  }, [prefill]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await client.get(USERS_CONTROLLER);
      setUsers(response.users || []);
    } catch (err) {
      setError('Failed to fetch users: ' + err.message);
    }
  };

  const filteredUsers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return [];
    }

    return users.filter((user) => {
      const searchable = `${user.fullName || ''} ${user.email || ''}`.toLowerCase();
      return searchable.includes(query);
    });
  }, [searchTerm, users]);

  const shouldShowUserSuggestions = searchTerm.trim().length > 0;

  const handleUserToggle = (user) => {
    const email = user.email?.trim();
    if (!email) return;

    const normalized = user.id ? String(user.id) : email;
    setForm((previous) => {
      const selected = new Set(previous.selectedUserIds);
      if (selected.has(normalized)) {
        selected.delete(normalized);
      } else {
        selected.add(normalized);
      }

      const selectedEmails = users
        .filter((item) => {
          const itemKey = item.id ? String(item.id) : item.email;
          return selected.has(itemKey);
        })
        .map((item) => item.email)
        .filter(Boolean);

      return {
        ...previous,
        selectedUserIds: Array.from(selected),
        to: selectedEmails.join(', '),
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const recipients = (form.to || '').split(',').map((value) => value.trim()).filter(Boolean);
    if (recipients.length === 0) {
      setError('Please select at least one recipient.');
      return;
    }

    const htmlBody = buildEmailTemplate(form.template, form.subject || 'Email message', form.message);

    try {
      setIsSending(true);
      const response = await fetch(`${process.env.REACT_APP_DB_BASE_URL || 'http://localhost/api'}/${EMAIL_CONTROLLER}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: form.subject || 'Email message',
          htmlBody,
          recipients,
        }),
      });

      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      setSuccess(`Email sent to ${result.total || recipients.length} recipient(s).`);
      setForm((previous) => ({
        ...previous,
        to: '',
        subject: '',
        template: 'RPSwiss',
        message: 'Hello,\n\nWe hope you are well.',
        selectedUserIds: [],
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="admin-view-wrapper">
      <div className="admin-view-header">
        <h2 className="admin-title">Send Email</h2>
        {error && <div className="admin-error">{error}</div>}
        {success && <div className="admin-success">{success}</div>}
      </div>

      <form onSubmit={handleSubmit} className="admin-form-container email-send-form">
        <div className="form-group">
          <label>User search</label>
          <input
            type="text"
            placeholder="Search users by name or email"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>

        {shouldShowUserSuggestions && (
          <div className="form-group">
            <label>Recipients</label>
            <div className="user-tag-list">
              {filteredUsers.length === 0 ? (
                <div className="no-records" style={{ padding: '16px 12px', margin: 0 }}>No users found.</div>
              ) : (
                filteredUsers.map((user) => {
                  const email = user.email?.trim();
                  const isSelected = form.selectedUserIds.includes(String(user.id)) || form.to.includes(email || '');
                  if (!email) return null;

                  return (
                    <button
                      key={user.id}
                      type="button"
                      className={`user-tag ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleUserToggle(user)}
                    >
                      <span className="user-tag-name">{user.fullName || 'Unnamed user'}</span>
                      <span className="user-tag-email">{email}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        <div className="form-group">
          <label>To</label>
          <input
            type="text"
            placeholder="Recipients selected from the tag list"
            value={form.to}
            readOnly
          />
        </div>

        <div className="form-group">
          <label>Subject</label>
          <input
            type="text"
            placeholder="e.g. Hello"
            value={form.subject}
            onChange={(event) => setForm((previous) => ({ ...previous, subject: event.target.value }))}
          />
        </div>

        <div className="form-group">
          <label>Template</label>
          <select
            value={form.template}
            onChange={(event) => setForm((previous) => ({ ...previous, template: event.target.value }))}
          >
            {EMAIL_TEMPLATE_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Message</label>
          <textarea
            rows={8}
            value={form.message}
            onChange={(event) => setForm((previous) => ({ ...previous, message: event.target.value }))}
          />
        </div>

        <div className="form-footer" style={{ justifyContent: 'flex-end' }}>
          <button type="submit" className="btn btn-submit-professionals" disabled={isSending}>
            {isSending ? 'Sending...' : 'Send Email'}
          </button>
        </div>
      </form>
    </div>
  );
}
